import React, {
  Suspense,
  useCallback,
  useEffect,
  useLayoutEffect,
  useRef,
  useMemo,
  useState
} from 'react';
import { useOutletContext } from 'react-router-dom';
import classNames from 'classnames';
import { MediaViewerOutletContextState } from '@layouts/types';
import { useAssetMVPermissions, useTypedSelector } from '@hooks';
import PlaybackPreparingLoader from '@pages/MediaViewer/AssetViewer/PlaybackPreparingLoader';
import { UxpPremierePlayheadSync } from '@pages/MediaViewer/UxpPremierePlayheadSync';
import { AssetViewerStateProvider } from '@context/AssetViewerStateContext';
import { getBrowser, getOS } from '@helpers/withKeysCanvasHistoryChange';
import CompareVersionsModalContainer from '@pages/MediaViewer/components/CompareVersionsModal/CompareVersionsModalContainer';
import FloatCommentEditor from '@pages/MediaViewer/components/FloatCommentEditor';
import FloatCommentItem from '@pages/MediaViewer/components/FloatCommentItem';
import TranscriptDrawerContainer from '@pages/MediaViewer/components/TranscriptDrawer';
import MediaViewerSidebar from '@pages/MediaViewer/components/MediaViewerSidebar';
import { useResponsive } from '@hooks/useResponsive';
import { LgWidth } from '@components/Responsive';
import SidebarPanelDismissHandler from '@pages/MediaViewer/components/SidebarPanelDismissHandler';
import { useMobileSidebarBottomSheetDrag } from '@pages/MediaViewer/hooks/useMobileSidebarBottomSheetDrag';
import { assetVersionStateSelector } from '@redux/selectors/tasks';
import './MediaViewer.less';
import { IS_UXP_WEBVIEW } from '@config/constants';
import {
  MediaViewerCommentsIcon,
  MediaViewerFileInfoIcon,
  TasksIcon
} from '@icons';

const FabricContextProvider = React.lazy(
  () => import('@context/FabricContext')
);
const AssetViewer = React.lazy(() => import('@pages/MediaViewer/AssetViewer'));
const AssetsSlider = React.lazy(
  () => import('@pages/MediaViewer/components/AssetsSlider')
);

type MediaViewerProps = {
  commentMentions: boolean;
  isLoadingAsset?: boolean;
};

const SIDEBAR_MOBILE_PEEK_MIN_PX = 75;
const SIDEBAR_WHEEL_PEEK_STEP_MAX_PX = 120;

function getKeysMediaViewerShortcuts() {
  const macos = {
    comments: 'Shift + Option + 1',
    tasks: 'Shift + Option + 2',
    fileInfo: 'Shift + Option + 3'
  };
  const windows = {
    comments: 'Shift + Alt + 1',
    tasks: 'Shift + Alt + 2',
    fileInfo: 'Shift + Alt + 3'
  };
  const os = getOS();
  const browser = getBrowser();
  if (os === 'macos') {
    if (browser === 'safari') return macos;
    if (browser === 'chrome') return macos;
  }
  if (os === 'windows') return windows;
  return windows;
}

function MediaViewer({
  commentMentions,
  isLoadingAsset = false
}: MediaViewerProps) {
  const {
    commentsList,
    commentsLastReadAt,
    markCommentsRead,
    isLoadingCommentsList,
    tasksLastReadAt,
    latestTaskCreatedAt,
    markTasksRead
  } = useOutletContext<MediaViewerOutletContextState>();
  const { isMdWidth, isLgWidth } = useResponsive();
  const sidebarRef = useRef<HTMLDivElement>(null);
  const viewerContainerRef = useRef<HTMLDivElement>(null);
  const [isPanelPinned, setIsPanelPinned] = useState(false);
  const sidebarPeekRef = useRef(SIDEBAR_MOBILE_PEEK_MIN_PX);
  const isEmbeddedWebView = IS_UXP_WEBVIEW;

  const canViewTasks = useAssetMVPermissions('listTasks');
  const canCreateTask = useAssetMVPermissions('convertCommentToTask');

  const canReadComment = useAssetMVPermissions('readComments');
  const canLeaveComment = useAssetMVPermissions('comment');
  const selectedVersion = useTypedSelector(
    ({ mediaViewer }) => mediaViewer.selectedVersion
  );
  const asset = useTypedSelector(({ mediaViewer }) => mediaViewer.assets);
  const [selectedCollapse, setSelectedCollapse] = useState<string | null>(null);

  useEffect(() => {
    if (canReadComment || canLeaveComment) setSelectedCollapse('comments');
  }, [canReadComment, canLeaveComment]);

  const isShowFloatCommentForm = asset && canLeaveComment && !!selectedVersion;

  const isShowFloatCommentItem =
    asset &&
    !!selectedVersion &&
    (selectedCollapse !== 'comments' || isMdWidth);

  const latestCommentCreatedAt = useMemo(() => {
    return commentsList
      .filter((x) => !x.user?.me)
      .reduce<string | null>((latest, comment) => {
        if (!latest) return comment.createdAt;
        return new Date(comment.createdAt) > new Date(latest)
          ? comment.createdAt
          : latest;
      }, null);
  }, [commentsList]);

  const tasksCount = useTypedSelector((state) =>
    selectedVersion
      ? assetVersionStateSelector(state, { assetVersionId: selectedVersion.id })
          .count
      : 0
  );

  const hasUnreadComments = useMemo(() => {
    if (!latestCommentCreatedAt) return false;
    if (!commentsLastReadAt) return true;
    return new Date(commentsLastReadAt) < new Date(latestCommentCreatedAt);
  }, [commentsLastReadAt, latestCommentCreatedAt]);

  const hasUnreadTasks = useMemo(() => {
    if (!latestTaskCreatedAt) return false;
    if (!tasksLastReadAt) return true;
    return new Date(tasksLastReadAt) < new Date(latestTaskCreatedAt);
  }, [tasksLastReadAt, latestTaskCreatedAt]);

  const sidebarMenuItems = useMemo(() => {
    const keys = getKeysMediaViewerShortcuts();
    return [
      {
        key: 'comments',
        icon: <MediaViewerCommentsIcon />,
        hasUnreadComments: selectedCollapse !== 'comments' && hasUnreadComments,
        tooltip: (
          <>
            Comments Panel
            <span className="media-viewer-tooltip-label">{keys.comments}</span>
          </>
        )
      },
      ...(!isEmbeddedWebView && canViewTasks
        ? [
            {
              key: 'tasks',
              icon: <TasksIcon />,
              className: 'tour-create-task-from-comment-tasks',
              hasUnreadComments: selectedCollapse !== 'tasks' && hasUnreadTasks,
              tooltip: (
                <>
                  Tasks Panel
                  <span className="media-viewer-tooltip-label">
                    {keys.tasks}
                  </span>
                </>
              )
            }
          ]
        : []),
      {
        key: 'fileInfo',
        icon: <MediaViewerFileInfoIcon />,
        tooltip: (
          <>
            Details Panel
            <span className="media-viewer-tooltip-label">{keys.fileInfo}</span>
          </>
        )
      }
    ];
  }, [canViewTasks, selectedCollapse, hasUnreadComments, hasUnreadTasks]);

  const onToggleOpenSelectedCollapse = useCallback(
    (value: string | null) => {
      setSelectedCollapse((prevSelectedCollapse) => {
        if (prevSelectedCollapse === value) return null;
        return value;
      });
    },
    [setSelectedCollapse]
  );

  const onTogglePanelPin = useCallback(() => {
    setIsPanelPinned((prev) => !prev);
  }, []);

  useEffect(() => {
    const onKeyPress = (e: KeyboardEvent) => {
      const isShortcutModifier = e.shiftKey && e.altKey;
      const keyCode = e.code;
      if (!isShortcutModifier) return;
      if (keyCode === 'Digit1') {
        onToggleOpenSelectedCollapse('comments');
        return;
      }
      if (keyCode === 'Digit2' && canViewTasks) {
        onToggleOpenSelectedCollapse('tasks');
        return;
      }
      if (keyCode === 'Digit3') {
        onToggleOpenSelectedCollapse('fileInfo');
      }
    };

    document.addEventListener('keydown', onKeyPress);
    return () => {
      document.removeEventListener('keydown', onKeyPress);
    };
  }, [canViewTasks, onToggleOpenSelectedCollapse]);

  const isCommentsOpened = selectedCollapse === 'comments';
  const _markCommentsRead = () => {
    if (!selectedVersion) return;
    markCommentsRead(selectedVersion.id);
  };
  const markCommentsReadRef = useRef(_markCommentsRead);
  markCommentsReadRef.current = _markCommentsRead;
  useEffect(() => {
    if (isCommentsOpened) markCommentsReadRef.current();
  }, [isCommentsOpened, hasUnreadComments]);

  const isTasksOpened = selectedCollapse === 'tasks';
  const _markTasksRead = () => {
    if (!selectedVersion) return;
    markTasksRead(selectedVersion.id);
  };
  const markTasksReadRef = useRef(_markTasksRead);
  markTasksReadRef.current = _markTasksRead;
  useEffect(() => {
    if (isTasksOpened) markTasksReadRef.current();
  }, [isTasksOpened, hasUnreadTasks]);

  const applySidebarPeekPx = useCallback((px: number) => {
    const shell = sidebarRef.current;
    const container = viewerContainerRef.current;
    if (!shell || !container) return;
    const maxH = shell.offsetHeight;
    if (maxH < SIDEBAR_MOBILE_PEEK_MIN_PX) return;
    const clamped = Math.min(
      maxH,
      Math.max(SIDEBAR_MOBILE_PEEK_MIN_PX, Math.round(px))
    );
    sidebarPeekRef.current = clamped;
    container.style.setProperty(
      '--media-viewer-bottom-sheet-visible-height',
      `${clamped}px`
    );
  }, []);

  useEffect(() => {
    if (!isMdWidth) {
      sidebarPeekRef.current = SIDEBAR_MOBILE_PEEK_MIN_PX;
      viewerContainerRef.current?.style.removeProperty(
        '--media-viewer-bottom-sheet-visible-height'
      );
    }
  }, [isMdWidth]);

  useLayoutEffect(() => {
    if (!isMdWidth || !sidebarRef.current) return;
    const el = sidebarRef.current;
    const clampPeek = () => {
      applySidebarPeekPx(sidebarPeekRef.current);
    };
    clampPeek();
    const ro = new ResizeObserver(clampPeek);
    ro.observe(el);
    return () => ro.disconnect();
  }, [isMdWidth, applySidebarPeekPx]);

  const { onSidebarHandlePointerDown } = useMobileSidebarBottomSheetDrag({
    enabled: isMdWidth,
    peekRef: sidebarPeekRef,
    applyPeek: applySidebarPeekPx,
    containerRef: viewerContainerRef
  });

  const onChangeCollapse = useCallback(
    (value: string | null) => {
      setSelectedCollapse(value);
      if (!isMdWidth || !value || !sidebarRef.current) return;
      const isSidebarHidden =
        sidebarPeekRef.current <= SIDEBAR_MOBILE_PEEK_MIN_PX;
      if (!isSidebarHidden) return;
      applySidebarPeekPx(sidebarRef.current.offsetHeight);
    },
    [isMdWidth, applySidebarPeekPx]
  );

  const onViewerContainerWheel = useCallback(
    (e: React.WheelEvent<HTMLDivElement>) => {
      if (!isMdWidth || (!canReadComment && !canLeaveComment)) return;

      const shell = sidebarRef.current;
      if (!shell || e.currentTarget !== viewerContainerRef.current) return;
      const target = e.target as HTMLElement;
      if (
        target.closest('.panel-section') ||
        target.closest('.media_viewer_menu')
      ) {
        return;
      }

      const maxH = shell.offsetHeight;
      if (maxH < SIDEBAR_MOBILE_PEEK_MIN_PX) return;
      const step =
        Math.sign(e.deltaY) *
        Math.min(Math.abs(e.deltaY), SIDEBAR_WHEEL_PEEK_STEP_MAX_PX);
      const nextPeek = sidebarPeekRef.current + step;

      if (step < 0 && sidebarPeekRef.current <= SIDEBAR_MOBILE_PEEK_MIN_PX) {
        return;
      }
      if (step > 0 && sidebarPeekRef.current >= maxH) {
        return;
      }

      e.preventDefault();
      applySidebarPeekPx(nextPeek);
    },
    [isMdWidth, canReadComment, canLeaveComment, applySidebarPeekPx]
  );

  const loader = (
    <div className="media_viewer_container">
      <div className="media_viewer_content">
        <PlaybackPreparingLoader />
      </div>
      <div className="media_viewer_sidebar" />
    </div>
  );

  if (!asset || !selectedVersion || isLoadingAsset) return loader;
  const viewerStateKey = `${asset?.id ?? ''}-${selectedVersion?.id ?? ''}`;

  const mobileAction = asset
    ? {
        commentMentions,
        canCreateTask,
        isShowFloatCommentForm: !!isShowFloatCommentForm,
        isShowFloatCommentItem: !!isShowFloatCommentItem
      }
    : undefined;

  return (
    <Suspense fallback={loader}>
      <div
        ref={viewerContainerRef}
        onWheel={onViewerContainerWheel}
        className={classNames({
          media_viewer_container: true,
          'b-content-full': !canReadComment && !canLeaveComment,
          'b-task-hide': !canViewTasks
        })}
      >
        <AssetViewerStateProvider
          key={viewerStateKey}
          selectedVersion={selectedVersion}
          commentsList={commentsList ?? []}
        >
          <UxpPremierePlayheadSync />
          <SidebarPanelDismissHandler
            sidebarRef={sidebarRef}
            isOpen={!!selectedCollapse}
            isPinned={isPanelPinned}
            isLgWidth={isLgWidth}
            onClose={() => onToggleOpenSelectedCollapse(null)}
          />
          <FabricContextProvider>
            <>
              <div
                className={classNames('media_viewer_content', {
                  'media_viewer_content-has-sidebar': !!selectedCollapse
                })}
              >
                <TranscriptDrawerContainer />
                {!isEmbeddedWebView && <AssetsSlider />}
                <AssetViewer
                  type="main"
                  asset={asset}
                  selectedVersion={selectedVersion}
                  setSelectedCollapse={onChangeCollapse}
                  isDesktopSidebarOpen={!!selectedCollapse && !isMdWidth}
                  mobileAction={mobileAction}
                  isEmbeddedWebView={isEmbeddedWebView}
                />
                <LgWidth>
                  {asset && isShowFloatCommentForm && (
                    <FloatCommentEditor
                      asset={asset}
                      canCreateTask={canCreateTask}
                      commentMentions={commentMentions}
                      selectedVersion={selectedVersion}
                      setSelectedCollapse={onChangeCollapse}
                    />
                  )}
                  {asset && isShowFloatCommentItem && (
                    <FloatCommentItem
                      asset={asset}
                      selectedVersion={selectedVersion}
                      commentMentions={commentMentions}
                      canCreateTask={canCreateTask}
                      setSelectedCollapse={onChangeCollapse}
                    />
                  )}
                </LgWidth>
              </div>
              {asset && (canReadComment || canLeaveComment) && (
                <MediaViewerSidebar
                  sidebarRef={sidebarRef}
                  selectedCollapse={selectedCollapse}
                  isPanelPinned={isPanelPinned}
                  isShowFloatCommentForm={isShowFloatCommentForm}
                  isShowFloatCommentItem={!!isShowFloatCommentItem}
                  onToggleOpenSelectedCollapse={onToggleOpenSelectedCollapse}
                  onTogglePanelPin={onTogglePanelPin}
                  setSelectedCollapse={onChangeCollapse}
                  sidebarMenuItems={sidebarMenuItems}
                  canViewTasks={canViewTasks}
                  canCreateTask={canCreateTask}
                  selectedVersion={selectedVersion}
                  asset={asset}
                  commentsList={commentsList}
                  isLoadingCommentsList={isLoadingCommentsList}
                  commentMentions={commentMentions}
                  tasksCount={tasksCount}
                  onSidebarHandlePointerDown={onSidebarHandlePointerDown}
                  isEmbeddedWebView={isEmbeddedWebView}
                />
              )}
            </>
          </FabricContextProvider>
        </AssetViewerStateProvider>
        <CompareVersionsModalContainer commentMentions={commentMentions} />
      </div>
    </Suspense>
  );
}

export default MediaViewer;
